import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaPaperPlane, FaPlus, FaSave, FaTrash } from "react-icons/fa";
import { useNotify } from "../../components/ConfirmModal";
import { PageHead } from "../../pages/work/WorkUi";
import PortalSelect from "../PortalSelect";
import billingApi, { billingError } from "./billingApi";
import { money, todayIso, useBillingMeta } from "./BillingUi";
import "../../pages/dm/dm.css";
import "../../pages/work/work.css";
import "./billing.css";

const blankItem = (tax) => ({ Description: "", Quantity: "1", Unit_Price: "", Tax_Pct: tax ?? "" });

function addDays(iso, days) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + Number(days || 0));
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

const n = (v) => {
  const x = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(x) ? x : 0;
};

/** New invoice / edit a draft: client (and project or service), lines with tax, discount, dates, notes. */
function TenantClientInvoiceEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const { meta, error: metaError } = useBillingMeta();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const editing = Boolean(id);

  useEffect(() => {
    if (!meta) return;
    const s = meta.settings || {};
    if (editing) {
      billingApi
        .invoice(id)
        .then((inv) => {
          if (inv.Status !== "Draft") {
            navigate(`/portal/finance/client-invoices/${id}`, { replace: true });
            return;
          }
          setForm({
            Tenant_Client_Id: String(inv.Tenant_Client_Id),
            Tenant_Project_Id: inv.Tenant_Project_Id ? String(inv.Tenant_Project_Id) : "",
            Tenant_Client_Service_Id: inv.Tenant_Client_Service_Id ? String(inv.Tenant_Client_Service_Id) : "",
            Invoice_Date: inv.Invoice_Date,
            Due_Date: inv.Due_Date || "",
            Discount_Amount: Number(inv.Discount_Amount) ? inv.Discount_Amount : "",
            Bill_To_GSTIN: inv.Bill_To?.GSTIN || "",
            Notes: inv.Notes || "",
            Terms: inv.Terms || "",
            items: inv.items.map((it) => ({
              Description: it.Description,
              Quantity: it.Quantity,
              Unit_Price: it.Unit_Price,
              Tax_Pct: it.Tax_Pct ?? "",
            })),
          });
        })
        .catch((err) => setError(billingError(err, "Could not load the invoice")));
      return;
    }
    const today = todayIso();
    setForm({
      Tenant_Client_Id: params.get("client") || "",
      Tenant_Project_Id: "",
      Tenant_Client_Service_Id: "",
      Invoice_Date: today,
      Due_Date: addDays(today, s.Due_Days ?? 15),
      Discount_Amount: "",
      Bill_To_GSTIN: "",
      Notes: "",
      Terms: s.Terms || "",
      items: [blankItem(s.Default_Tax_Pct)],
    });
  }, [meta, editing, id, params, navigate]);

  const client = useMemo(
    () => (meta?.clients || []).find((c) => String(c.Tenant_Client_Id) === String(form?.Tenant_Client_Id)),
    [meta, form?.Tenant_Client_Id]
  );
  const currency = meta?.settings?.Currency;
  const taxLabel = meta?.settings?.Tax_Label || "Tax";

  const totals = useMemo(() => {
    const rows = form?.items || [];
    const sub = rows.reduce((s, r) => s + n(r.Quantity) * n(r.Unit_Price), 0);
    const tax = rows.reduce((s, r) => s + (n(r.Quantity) * n(r.Unit_Price) * n(r.Tax_Pct)) / 100, 0);
    const discount = n(form?.Discount_Amount);
    return { sub, tax, discount, total: sub + tax - discount };
  }, [form]);

  if (!meta || !form) {
    return (
      <div className="dmp wk bl">
        {metaError || error ? <div className="dmp-error">{metaError || error}</div> : <div className="wk-muted">Loading…</div>}
      </div>
    );
  }

  if (!meta.access?.manage) {
    return (
      <div className="dmp wk bl">
        <div className="dmp-error">Your role cannot raise invoices. Ask your company owner.</div>
      </div>
    );
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setItem = (i, k) => (e) =>
    setForm((f) => ({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, blankItem(meta.settings?.Default_Tax_Pct)] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }));

  const payload = (issue) => ({
    Tenant_Client_Id: form.Tenant_Client_Id,
    Tenant_Project_Id: form.Tenant_Project_Id || null,
    Tenant_Client_Service_Id: form.Tenant_Client_Service_Id || null,
    Invoice_Date: form.Invoice_Date,
    Due_Date: form.Due_Date || null,
    Discount_Amount: form.Discount_Amount || 0,
    Bill_To_GSTIN: form.Bill_To_GSTIN,
    Notes: form.Notes,
    Terms: form.Terms,
    items: form.items.map((it) => ({
      Description: it.Description,
      Quantity: it.Quantity || 1,
      Unit_Price: it.Unit_Price || 0,
      Tax_Pct: it.Tax_Pct === "" ? null : it.Tax_Pct,
    })),
    issue,
  });

  const save = async (issue) => {
    setSaving(true);
    setError("");
    try {
      let inv;
      if (editing) {
        inv = await billingApi.updateInvoice(id, payload(false));
        if (issue) inv = await billingApi.issueInvoice(id);
      } else {
        inv = await billingApi.createInvoice(payload(issue));
      }
      notify.success(issue ? `Invoice ${inv.Invoice_No} issued` : "Draft saved");
      navigate(`/portal/finance/client-invoices/${inv.Invoice_Id}${issue ? "?share=1" : ""}`);
    } catch (err) {
      setError(billingError(err, "Could not save the invoice"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dmp wk bl">
      <Link to="/portal/finance/invoices" className="dmp-back-link">
        <FaArrowLeft aria-hidden="true" /> Invoices
      </Link>
      <PageHead
        title={editing ? "Edit draft invoice" : "New invoice"}
        info="Save a draft to finish later. Issuing gives it the next invoice number and locks it; to change an issued invoice, cancel it and duplicate it."
      />
      {error ? <div className="dmp-error">{error}</div> : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save(true);
        }}
      >
        <div className="dmp-card">
          <h3 className="dmp-card__title">Bill to</h3>
          <div className="bl-form-grid">
            <label className="wk-field">
              Client
              <PortalSelect
                value={form.Tenant_Client_Id}
                onChange={(e) => setForm((f) => ({ ...f, Tenant_Client_Id: e.target.value, Tenant_Project_Id: "", Tenant_Client_Service_Id: "" }))}
                aria-label="Client"
                required
                searchable
              >
                <option value="">Pick a client</option>
                {meta.clients.map((c) => (
                  <option key={c.Tenant_Client_Id} value={c.Tenant_Client_Id}>
                    {c.Client_Name}
                  </option>
                ))}
              </PortalSelect>
              {!meta.clients.length ? (
                <span className="wk-field__hint">
                  No clients yet. <Link to="/portal/clients/new">Add a client</Link> first.
                </span>
              ) : client ? (
                <span className="wk-field__hint">
                  {[client.Contact_Person, client.Email, client.Mobile_Number].filter(Boolean).join(" · ") || "No contact details"}
                </span>
              ) : null}
            </label>
            {client?.projects?.length ? (
              <label className="wk-field">
                Project (optional)
                <PortalSelect value={form.Tenant_Project_Id} onChange={set("Tenant_Project_Id")} aria-label="Project">
                  <option value="">No project</option>
                  {client.projects.map((p) => (
                    <option key={p.Tenant_Project_Id} value={p.Tenant_Project_Id}>
                      {p.Name}
                    </option>
                  ))}
                </PortalSelect>
              </label>
            ) : null}
            {client?.services?.length ? (
              <label className="wk-field">
                Service (optional)
                <PortalSelect value={form.Tenant_Client_Service_Id} onChange={set("Tenant_Client_Service_Id")} aria-label="Service">
                  <option value="">No service</option>
                  {client.services.map((s) => (
                    <option key={s.Tenant_Client_Service_Id} value={s.Tenant_Client_Service_Id}>
                      {s.Name}
                    </option>
                  ))}
                </PortalSelect>
              </label>
            ) : null}
            <label className="wk-field">
              Client GSTIN (optional)
              <input value={form.Bill_To_GSTIN} onChange={set("Bill_To_GSTIN")} maxLength={20} placeholder="36ABCDE1234F1Z5" />
            </label>
            <label className="wk-field">
              Invoice date
              <input type="date" value={form.Invoice_Date} onChange={set("Invoice_Date")} required />
            </label>
            <label className="wk-field">
              Due date
              <input type="date" value={form.Due_Date} min={form.Invoice_Date} onChange={set("Due_Date")} />
            </label>
          </div>
        </div>

        <div className="dmp-card">
          <h3 className="dmp-card__title">What you are billing</h3>
          <table className="wk-table bl-lines">
            <thead>
              <tr>
                <th>Item or service</th>
                <th className="bl-num">Qty</th>
                <th className="bl-num">Rate</th>
                <th className="bl-num">{taxLabel} %</th>
                <th className="bl-num">Amount</th>
                <th>
                  <span className="wk-sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => {
                const amount = n(it.Quantity) * n(it.Unit_Price);
                return (
                  <tr key={i}>
                    <td data-label="Item or service">
                      <input
                        aria-label={`Line ${i + 1} description`}
                        value={it.Description}
                        onChange={setItem(i, "Description")}
                        placeholder="Social media management - October"
                        maxLength={500}
                        required
                      />
                    </td>
                    <td data-label="Qty" className="bl-num">
                      <input aria-label={`Line ${i + 1} quantity`} type="number" min="0.01" step="0.01" value={it.Quantity} onChange={setItem(i, "Quantity")} required />
                    </td>
                    <td data-label="Rate" className="bl-num">
                      <input aria-label={`Line ${i + 1} rate`} type="number" min="0" step="0.01" value={it.Unit_Price} onChange={setItem(i, "Unit_Price")} required />
                    </td>
                    <td data-label={`${taxLabel} %`} className="bl-num">
                      <input aria-label={`Line ${i + 1} tax percent`} type="number" min="0" max="100" step="0.01" value={it.Tax_Pct} onChange={setItem(i, "Tax_Pct")} placeholder="0" />
                    </td>
                    <td data-label="Amount" className="bl-num">
                      {money(amount + (amount * n(it.Tax_Pct)) / 100, currency)}
                    </td>
                    <td>
                      {form.items.length > 1 ? (
                        <button type="button" className="dmp-link" onClick={() => removeItem(i)} aria-label={`Remove line ${i + 1}`}>
                          <FaTrash aria-hidden="true" />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={addItem} style={{ marginTop: 8 }}>
            <FaPlus aria-hidden="true" /> Add line
          </button>

          <div className="bl-sum">
            <div>
              <span>Subtotal</span>
              <span>{money(totals.sub, currency)}</span>
            </div>
            <div>
              <span>{taxLabel}</span>
              <span>{money(totals.tax, currency)}</span>
            </div>
            <div className="bl-sum__discount">
              <label htmlFor="bl-discount">Discount</label>
              <input id="bl-discount" type="number" min="0" step="0.01" value={form.Discount_Amount} onChange={set("Discount_Amount")} placeholder="0" />
            </div>
            <div className="bl-sum__total">
              <span>Total</span>
              <span>{money(totals.total, currency)}</span>
            </div>
          </div>
        </div>

        <div className="dmp-card">
          <div className="bl-form-grid">
            <label className="wk-field bl-span-2">
              Notes for the client (optional)
              <textarea value={form.Notes} onChange={set("Notes")} rows={2} />
            </label>
            <label className="wk-field bl-span-2">
              Terms
              <textarea value={form.Terms} onChange={set("Terms")} rows={2} />
            </label>
          </div>
        </div>

        <div className="wk-actions-bar bl-sticky-actions">
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => save(false)} disabled={saving || !form.Tenant_Client_Id}>
            <FaSave aria-hidden="true" /> Save draft
          </button>
          <button type="submit" className="dmp-btn" disabled={saving || !form.Tenant_Client_Id || totals.total <= 0}>
            <FaPaperPlane aria-hidden="true" /> Issue invoice
          </button>
        </div>
      </form>
    </div>
  );
}

export default TenantClientInvoiceEditor;
